import { useTraditionalCharactersEnabled } from "@-/dev.improve.haniverse.lib.posthog";
import { useIsPremium } from "@-/dev.improve.haniverse.components.superwall";
import {
  morphemeDetailsTileSliceQueryFn,
  useGetMorphemeDetailsTileSliceQueryKey,
} from "@-/dev.improve.haniverse.lib.morpheme.read";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { queryOptions, type QueryClient } from "@-/com.npmjs.tanstack__react-query";
import {
  getGraphemeLessonPart,
  hardcodedPronunciationReadingAtSlot,
  type GraphemeLessonPart,
  type Level,
} from "@-/dev.improve.haniverse.lib.level";
import {
  useDeferredGraphemesQueryOptions,
  useStudiedGraphemesQueryOptions,
} from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.meaning.read";
import {
  type PronouncedGraphemes,
  pronouncedReadingKey,
  usePronouncedGraphemesQueryOptions,
} from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.pronunciation.read";
import { isGraphemeDeferralActive } from "@-/dev.improve.haniverse.lib.cards.user-card.update.deferred-until";
import { useLocalDayKey } from "@-/dev.improve.haniverse.lib.time.local-day";
import {
  getPendingCompletedSlideKeys,
  useCompletedSlideKeysQueryOptions,
} from "@-/dev.improve.haniverse.lib.slide.read";
import { useActorUserDataQueryOptions } from "@-/dev.improve.haniverse.lib.user";
import {
  getSimplifiedLevels,
  getTraditionalLevels,
} from "@-/dev.improve.haniverse.assets.graphemes.levels";
import { getAccessibleLevels } from "@-/dev.improve.haniverse.lib.entitlement.free-tier";
import {
  remainingDailyNewGraphemes,
  resolveDailyNewGraphemeLimit,
  useGraphemesIntroducedTodayQueryOptions,
} from "@-/dev.improve.haniverse.lib.learn.new-grapheme-limit";
import {
  getQuizManifest,
  getSlideManifest,
  isAfterAnchor,
  isEndAnchor,
  isStartAnchor,
  type QuizManifestEntry,
  type SlideAnchor,
  type SlideManifestEntry,
} from "@-/dev.improve.haniverse.assets.slides";
import { useAnsweredQuizKeysQueryOptions } from "@-/dev.improve.haniverse.lib.quiz.read";
import { uniq } from "@-/com.npmjs.array-utils-ts";
import arrayLength from "@-/com.npmjs.extra-array__length";
import memoize from "@-/com.npmjs.memoize";
import type { Unit } from "@-/dev.improve.haniverse.lib.lesson.entities";
import { useContentAvailability } from "@-/dev.improve.haniverse.lib.database.shards";

export type { Unit } from "@-/dev.improve.haniverse.lib.lesson.entities";

// How many fallback morpheme candidates resolve their grapheme card per parallel step after
// the first candidate is unavailable. Sized to match the
// `maxBatchSize` of the DataLoader behind `morphemeDetailsTileSliceQueryFn` (64), so one chunk
// maps to roughly one batched `WHERE grapheme IN (…)` read and stays well inside
// `SQLITE_MAX_VARIABLE_NUMBER` on the web binding. See PASS 2 in the resolver below.
const CANDIDATE_CHUNK_SIZE = 64;

const NEXT_LESSON_TABLES = [
  "user",
  "user_card",
  "user_grapheme_meaning_card",
  "user_grapheme_pronunciation_card",
  "user_slide",
  "user_quiz_card",
  "collection",
] as const;

// Resolve the slide the "Continue" button should advance to after `slideSlug`, or `null`
// when the next thing the learner is due is NOT a slide.
//
// The walk runs over the FULL lesson sequence, not a slide-only projection, because the
// sequence is what encodes where each slide's teaching moment sits relative to the
// characters. A run of slides sharing one anchor still pages together — they are adjacent
// in the sequence — but the run STOPS at the next unlearned character, and the caller falls
// back to {@link useNextLessonQueryOptions}, which returns that character's lesson.
//
// This is the difference between "here is what a character component is" landing after 一
// and landing before the learner has ever seen 一. Filtering the sequence down to slides
// discards every morpheme boundary, so the walk could not stop at a character even in
// principle: from the welcome slide it paged straight through the level-1 slides — the
// `after: 一` and `after: 二` ones included — and only handed over once the catalogue ran
// out. Ordering was never the problem; throwing the ordering away at the point of use was.
//
// `studiedGraphemes` marks which characters count as already learned. A character the
// learner has studied is not a boundary — they are past it — so the walk continues through
// it to the slides beyond. Slides they have moved past are skipped outright (see
// {@link findStaleSlideKeys}). Omit it only where staleness and boundaries are irrelevant
// (e.g. pure preloading); with it omitted every character is treated as a stopping point.
//
// `pronouncedGraphemes` does the same for pronunciation-level lessons (see
// {@link isSequenceEntryLearned}).
export function getNextSlideSlugInOrder(
  slideSlug: string,
  levels: Level[],
  studiedGraphemes?: Set<string>,
  pronouncedGraphemes?: PronouncedGraphemes | ReadonlySet<string>,
): string | null {
  return nextSlideSlugInSequence(
    buildUnitSequence(levels),
    slideSlug,
    studiedGraphemes,
    asPronouncedGraphemes(pronouncedGraphemes),
  );
}

function asPronouncedGraphemes(
  value: PronouncedGraphemes | ReadonlySet<string> | undefined,
): PronouncedGraphemes {
  if (value === undefined) {
    return { graphemes: new Set(), readings: new Set() };
  }
  if (typeof value === "object" && value !== null && "graphemes" in value && "readings" in value) {
    return value;
  }
  return { graphemes: value, readings: new Set() };
}

/**
 * Resolve the zero-based grapheme-level index a lesson belongs to.
 *
 * Morphemes already carry their concrete {@link Level}. Slides and quizzes instead carry the normalized
 * index derived from their authored sequence position: level-1 slides (including onboarding
 * `before` 丨) are `0`, level-2 slides are `1`, and a `start.level` of `0` would be `-1`.
 */
export function getLessonLevelIndex(lesson: NextLessonCandidate): number {
  return lesson.type === "morpheme" ? lesson.level.index : lesson.levelIndex;
}

/**
 * The lesson group a pronunciation-level card belongs to: one past the level's own groups, so the
 * pronunciation level (`2-P1`) opens a lesson group of its own after its level's characters.
 */
export function getPronunciationLessonGroupIndex(level: Level): number {
  return level.lessonGroups?.length ?? 1;
}

/** The lesson group a card unit belongs to within its level. */
export function getMorphemeLessonGroupIndex({
  grapheme,
  level,
  part,
}: {
  grapheme: string;
  level: Level;
  part?: GraphemeLessonPart;
}): number {
  return part === "pronunciation"
    ? getPronunciationLessonGroupIndex(level)
    : getGraphemeLessonGroupIndex(level, grapheme);
}

/** Resolve an authored slide slug to the same lesson metadata returned by the query resolver. */
export function getSlideUnit(
  slideSlug: string,
  levels: Level[],
): Extract<Unit, { type: "slide" }> | null {
  const entry = buildUnitSequence(levels).find(
    (candidate): candidate is Extract<SequenceEntry, { type: "slide" }> =>
      candidate.type === "slide" && candidate.slug === slideSlug,
  );
  return entry === undefined ? null : anchoredUnitFromSequenceEntry(entry);
}

/**
 * Resolve an authored quiz slug to the same lesson metadata returned by the query resolver, or
 * `null` when the active script's sequence does not place it.
 */
export function getQuizUnit(
  quizSlug: string,
  levels: Level[],
): Extract<Unit, { type: "quiz" }> | null {
  const entry = buildUnitSequence(levels).find(
    (candidate): candidate is Extract<SequenceEntry, { type: "quiz" }> =>
      candidate.type === "quiz" && candidate.slug === quizSlug,
  );
  return entry === undefined ? null : anchoredUnitFromSequenceEntry(entry);
}

/** Resolve a character within the generated lesson-group partition (legacy levels form one group). */
export function getGraphemeLessonGroupIndex(level: Level, grapheme: string): number {
  return Math.max(0, level.lessonGroups?.findIndex((group) => group.includes(grapheme)) ?? 0);
}

/** Slides after a character finish its lesson group; before/start slides open the next group. */
export function isLessonGroupBoundary({
  currentLevelIndex,
  currentLessonGroupIndex,
  nextLesson,
}: {
  currentLevelIndex: number;
  currentLessonGroupIndex: number;
  nextLesson: Unit | null;
}): boolean {
  if (isLessonLevelBoundary({ currentLevelIndex, nextLesson })) {
    return true;
  }
  if (nextLesson === null || getLessonLevelIndex(nextLesson) !== currentLevelIndex) {
    return false;
  }
  const nextLessonGroupIndex =
    nextLesson.type !== "morpheme"
      ? (nextLesson.lessonGroupIndex ?? 0)
      : getMorphemeLessonGroupIndex({
          grapheme: nextLesson.graphemeCard.front,
          level: nextLesson.level,
          part: nextLesson.part,
        });
  return nextLessonGroupIndex > currentLessonGroupIndex;
}

/** A level ends when no lesson remains or the next lesson belongs to a later level. */
export function isLessonLevelBoundary({
  currentLevelIndex,
  nextLesson,
}: {
  currentLevelIndex: number;
  nextLesson: Unit | null;
}): boolean {
  return nextLesson === null || getLessonLevelIndex(nextLesson) > currentLevelIndex;
}

/**
 * Whether the next unit belongs to a lesson group the learner has not started.
 *
 * Used where no current unit is mounted (the Learn summary). Looking up by `Level.index`
 * rather than array offset keeps the check valid if the catalogue ever stops being contiguous.
 * Intro slides have index `-1`, so they intentionally return false.
 */
export function isLessonInUnstartedLessonGroup({
  lesson,
  levels,
  pronouncedGraphemes,
  studiedGraphemes,
}: {
  lesson: NextLessonCandidate;
  levels: Level[];
  pronouncedGraphemes: PronouncedGraphemes | ReadonlySet<string>;
  studiedGraphemes: Set<string>;
}): boolean {
  const level = levels.find((candidate) => candidate.index === getLessonLevelIndex(lesson));
  if (level === undefined) {
    return false;
  }
  const pronouncedGraphemeSet = asPronouncedGraphemes(pronouncedGraphemes).graphemes;
  if (lesson.type === "morpheme" && lesson.part === "pronunciation") {
    return !(level.pronunciationCharacters ?? []).some((character) =>
      pronouncedGraphemeSet.has(character),
    );
  }
  const lessonGroupIndex =
    lesson.type !== "morpheme"
      ? (lesson.lessonGroupIndex ?? 0)
      : getGraphemeLessonGroupIndex(level, lesson.graphemeCard.front);
  const characters = level.lessonGroups?.[lessonGroupIndex] ?? level.characters;
  return !characters.some((character) => studiedGraphemes.has(character));
}

/**
 * The sequence-taking core of {@link getNextSlideSlugInOrder}, split out so it can be tested
 * against a hand-built sequence: under jest the authored catalogue is empty (`require.context`
 * is Metro-only), so a test driven through the live levels can only ever observe `null`.
 */
export function nextSlideSlugInSequence(
  sequence: readonly SequenceEntry[],
  slideSlug: string,
  studiedGraphemes?: Set<string>,
  pronouncedGraphemes: PronouncedGraphemes | ReadonlySet<string> = new Set(),
): string | null {
  const studied = studiedGraphemes ?? new Set<string>();
  const pronounced = asPronouncedGraphemes(pronouncedGraphemes);
  const staleKeys =
    studiedGraphemes === undefined
      ? new Set<string>()
      : findStaleSlideKeys(sequence, studied, pronounced);

  const currentIndex = sequence.findIndex(
    (entry) => entry.type === "slide" && entry.slug === slideSlug,
  );
  if (currentIndex === -1) {
    return null;
  }

  // The current slide may itself be stale (the learner opened it from a non-sequence
  // surface, e.g. favorites) — that never blocks advancing, only the DESTINATION is filtered.
  for (const entry of sequence.slice(currentIndex + 1)) {
    if (entry.type === "morpheme") {
      // An unstudied character is the learner's next lesson, so the slide run ends here and
      // the resolver takes over. A studied one is already behind them — keep walking.
      if (!isSequenceEntryLearned(entry, studied, pronounced)) {
        return null;
      }
      continue;
    }

    if (staleKeys.has(entry.key)) {
      continue;
    }
    // A quiz still ahead of the learner ends the slide run too: the resolver serves it (or, once it
    // is answered, whatever follows it).
    return entry.type === "slide" ? entry.slug : null;
  }
  return null;
}

// All slides in the SAME level that come after `slideSlug`, in sequence order. Used by the slide
// page to off-screen-preload every upcoming slide in the level — slides carry no async data and
// render synchronously from the bundled catalogue, so preloading the whole level is cheap and makes
// tapping "Continue" through it instant. Scoped to the level so we don't eagerly mount the entire
// (potentially large) slide catalogue. Returns `[]` if the slug is unknown or is the level's last.
export function getRemainingSlideSlugsInLevel(slideSlug: string, levels: Level[]): string[] {
  const slideEntries = buildUnitSequence(levels).filter(
    (entry): entry is Extract<SequenceEntry, { type: "slide" }> => entry.type === "slide",
  );

  const current = slideEntries.find((entry) => entry.slug === slideSlug);
  if (current === undefined) {
    return [];
  }

  return slideEntries
    .filter(
      (entry) =>
        // Same level, and later in the resolved sequence than the current slide.
        entry.sortKey[0] === current.sortKey[0] &&
        compareSortKeys(entry.sortKey, current.sortKey) > 0,
    )
    .map((entry) => entry.slug);
}

// Every lesson carries a flat sort key `[level, step, kind, order]`:
//   - `level`: the level the lesson belongs to. Morphemes use `Level.index + 1` so the
//     first grapheme level is `1`, leaving `0` for the intro slides that precede it.
//     A slide inherits the level of whatever it is anchored to.
//   - `step`: the 1-based position within the level. Morphemes use their index in the
//     level's character list; a slide inherits the step of its anchor character.
//   - `kind`: `0` for slides and quizzes, `1` for morphemes, so a slide anchored `before` a
//     character sorts ahead of it. An `after` anchor instead targets the FOLLOWING
//     character's step, so it lands between the two.
//   - `order`: sequences the slide cards and quizzes sharing a step, so several can play
//     back-to-back between two characters: one running ordinal across both manifests, in
//     authored order, each anchor's slide card before its quizzes. 1-based. Morphemes always
//     use `0`.
//
// Slides are positioned by the frontmatter anchor declared in each `.mdx` (compiled
// into the checked-in slide manifest) rather than by a filename index — so an anchor
// keeps pointing at the same CHARACTER when the curriculum generator renumbers or
// reorders a level.
//
// A level's pronunciation level (`pronunciationCharacters`, labelled `2-P1` after Level 2) follows
// its characters: its lessons continue the steps past the last character, as morpheme entries
// with `part: "pronunciation"`. A slide anchored to one of a glyph's pronunciation cards
// (`card="pronunciation-<pinyin>"`) takes the step of the lesson that reveals that card — the
// pronunciation-level lesson for a glyph a meaning-only level taught.
type SortKey = [number, number, number, number];

export type SequenceEntry =
  | {
      character: string;
      level: Level;
      /** A pronunciation-level lesson: the character's readings in the authored syllabus. */
      part?: GraphemeLessonPart;
      /**
       * Single-reading 2-P1 slot (tone-number pinyin). Set for hardcoded opening lessons that share
       * a grapheme (干 gan4, then 干 gan1).
       */
      pinyin?: string;
      sortKey: SortKey;
      type: "morpheme";
    }
  | { key: string; slug: string; sortKey: SortKey; type: "slide"; lessonGroupIndex?: number }
  | { key: string; slug: string; sortKey: SortKey; type: "quiz"; lessonGroupIndex?: number };

/** A slide or quiz entry: a unit anchored in the sequence rather than a card. */
type AnchoredSequenceEntry = Extract<SequenceEntry, { type: "quiz" | "slide" }>;

function anchoredUnitFromSequenceEntry(
  entry: Extract<SequenceEntry, { type: "slide" }>,
): Extract<Unit, { type: "slide" }>;
function anchoredUnitFromSequenceEntry(
  entry: Extract<SequenceEntry, { type: "quiz" }>,
): Extract<Unit, { type: "quiz" }>;
function anchoredUnitFromSequenceEntry(
  entry: AnchoredSequenceEntry,
): Extract<Unit, { type: "quiz" | "slide" }>;
function anchoredUnitFromSequenceEntry(
  entry: AnchoredSequenceEntry,
): Extract<Unit, { type: "quiz" | "slide" }> {
  const unit = {
    // Grapheme `Level.index` 0 is sequence level 1 (onboarding is `before` the first glyph).
    levelIndex: entry.sortKey[0] - 1,
    slug: entry.slug,
    lessonGroupIndex: entry.lessonGroupIndex ?? 0,
  };
  return entry.type === "quiz" ? { ...unit, type: "quiz" } : { ...unit, type: "slide" };
}

/**
 * Whether the learner is past a morpheme entry: its meaning is studied, or, for a
 * pronunciation-level lesson, the reading that lesson introduces is in study (or any reading when
 * the lesson is not pinned to one pinyin).
 */
export function isSequenceEntryLearned(
  entry: Extract<SequenceEntry, { type: "morpheme" }>,
  studiedGraphemes: ReadonlySet<string>,
  pronounced: PronouncedGraphemes,
): boolean {
  if (entry.part !== "pronunciation") {
    return studiedGraphemes.has(entry.character);
  }
  if (entry.pinyin !== undefined) {
    return pronounced.readings.has(
      pronouncedReadingKey({ grapheme: entry.character, pinyin: entry.pinyin }),
    );
  }
  return pronounced.graphemes.has(entry.character);
}

function compareSortKeys(a: SortKey, b: SortKey): number {
  return a[0] - b[0] || a[1] - b[1] || a[2] - b[2] || a[3] - b[3];
}

/**
 * Where a lesson sits in the sequence: its level, 1-based step within it, and the unit a slide
 * anchored to it belongs to.
 */
type LessonPosition = { level: number; step: number; lessonGroupIndex: number };

/** The lessons a slide's glyph anchor can resolve to. */
type LessonPositions = {
  /**
   * Each character's own lesson, which reveals its meaning card — and its pronunciation cards too,
   * unless `meaningOnly` (a meaning-only level leaves them to the pronunciation level).
   */
  characters: Map<string, LessonPosition & { meaningOnly: boolean }>;
  characterEndByLevel: Map<number, { step: number; lessonGroupIndex: number }>;
  /** 1-based sequence level → step just past its last character/pronunciation slot. */
  endStepByLevel: Map<number, { step: number; lessonGroupIndex: number }>;
  /** Pronunciation lessons pinned to a reading (`grapheme\0pinyin` → position). */
  pronunciationReadings: Map<string, LessonPosition>;
  /** Each character's first pronunciation-level lesson (`2-P1`). */
  pronunciations: Map<string, LessonPosition>;
  pronunciationStartByLevel: Map<number, { step: number; lessonGroupIndex: number }>;
};

// The FIRST occurrence of each grapheme across all levels — the same "first occurrence
// wins" rule the resolver's dedup uses, so an anchor resolves to the lesson the user
// actually reaches. Card anchors with a pinyin resolve to the lesson that introduces that
// reading (干 gan4 vs 干 gan1).
function buildLessonPositions(levels: readonly Level[]): LessonPositions {
  const positions: LessonPositions = {
    characterEndByLevel: new Map(),
    characters: new Map(),
    endStepByLevel: new Map(),
    pronunciationReadings: new Map(),
    pronunciations: new Map(),
    pronunciationStartByLevel: new Map(),
  };
  for (const level of levels) {
    positions.characterEndByLevel.set(level.index + 1, {
      step: level.characters.length + 1,
      lessonGroupIndex: getGraphemeLessonGroupIndex(level, level.characters.at(-1) ?? ""),
    });
    if ((level.pronunciationCharacters?.length ?? 0) > 0) {
      positions.pronunciationStartByLevel.set(level.index + 1, {
        step: level.characters.length + 1,
        lessonGroupIndex: getPronunciationLessonGroupIndex(level),
      });
    }
    for (const [characterIndex, character] of level.characters.entries()) {
      if (!positions.characters.has(character)) {
        positions.characters.set(character, {
          level: level.index + 1,
          meaningOnly:
            getGraphemeLessonPart({ grapheme: character, isPronunciationLesson: false, level }) !==
            "meaning-and-pronunciation",
          step: characterIndex + 1,
          lessonGroupIndex: getGraphemeLessonGroupIndex(level, character),
        });
      }
    }
    for (const [pronunciationIndex, character] of (level.pronunciationCharacters ?? []).entries()) {
      const position: LessonPosition = {
        level: level.index + 1,
        step: level.characters.length + pronunciationIndex + 1,
        lessonGroupIndex: getPronunciationLessonGroupIndex(level),
      };
      if (!positions.pronunciations.has(character)) {
        positions.pronunciations.set(character, position);
      }
      const hardcoded = hardcodedPronunciationReadingAtSlot({
        afterLevel: level.index + 1,
        grapheme: character,
        slotIndex: pronunciationIndex,
      });
      if (hardcoded !== undefined) {
        const readingKey = pronouncedReadingKey({
          grapheme: character,
          pinyin: hardcoded.pinyin,
        });
        if (!positions.pronunciationReadings.has(readingKey)) {
          positions.pronunciationReadings.set(readingKey, position);
        }
      }
    }
    const lastStep = level.characters.length + (level.pronunciationCharacters?.length ?? 0);
    positions.endStepByLevel.set(level.index + 1, {
      step: lastStep + 1,
      lessonGroupIndex: level.lessonGroups?.length ?? 0,
    });
  }
  return positions;
}

/**
 * The lesson a glyph anchor targets: the glyph's own lesson, or — for a `card` anchor — the first
 * lesson that reveals that pronunciation card. A meaning-only lesson reveals none, so a glyph it
 * teaches is reached through its pronunciation-level lesson instead.
 */
function resolveGlyphAnchorPosition(
  target: { card?: { kind: "pronunciation"; pinyin: string }; glyph: string },
  positions: LessonPositions,
): LessonPosition | undefined {
  const characterPosition = positions.characters.get(target.glyph);
  if (target.card === undefined) {
    return characterPosition;
  }
  // An explicit reading lesson wins even when its glyph also has a combined character lesson.
  const readingPosition = positions.pronunciationReadings.get(
    pronouncedReadingKey({ grapheme: target.glyph, pinyin: target.card.pinyin }),
  );
  if (readingPosition !== undefined) {
    return readingPosition;
  }
  if (characterPosition !== undefined && !characterPosition.meaningOnly) {
    return characterPosition;
  }
  return positions.pronunciations.get(target.glyph);
}

// Resolve one slide card's or quiz's anchor to its sort key and unit, or `null` when it cannot
// be placed — which happens legitimately when a glyph anchor names a character absent from
// the ACTIVE script (e.g. a simplified-only anchor while the user reads traditional), or
// a card no lesson in that script reveals. Such a slide or quiz is simply omitted from that
// script's sequence rather than being parked at an arbitrary position.
function resolveAnchorPlacement(
  entry: { anchor: SlideAnchor; order: number },
  positions: LessonPositions,
): { sortKey: SortKey; lessonGroupIndex: number } | null {
  const { anchor, order } = entry;

  if (isStartAnchor(anchor)) {
    if (anchor.start.part === "pronunciation") {
      const start = positions.pronunciationStartByLevel.get(anchor.start.level);
      return start === undefined
        ? null
        : {
            sortKey: [anchor.start.level, start.step, 0, order],
            lessonGroupIndex: start.lessonGroupIndex,
          };
    }
    // Head of the level, before its first character (step 1, kind 0).
    return { sortKey: [anchor.start.level, 1, 0, order], lessonGroupIndex: 0 };
  }

  if (isEndAnchor(anchor)) {
    if (
      anchor.end.part === "pronunciation" &&
      !positions.pronunciationStartByLevel.has(anchor.end.level)
    ) {
      return null;
    }
    // After the last character and any pronunciation slots of this level.
    const end = (
      anchor.end.part === "character" ? positions.characterEndByLevel : positions.endStepByLevel
    ).get(anchor.end.level);
    if (end === undefined) {
      return null;
    }
    return {
      sortKey: [anchor.end.level, end.step, 0, order],
      lessonGroupIndex: end.lessonGroupIndex,
    };
  }

  const position = resolveGlyphAnchorPosition(
    isAfterAnchor(anchor) ? anchor.after : anchor.before,
    positions,
  );
  if (position === undefined) {
    return null;
  }

  // `before` shares the lesson's step and sorts ahead of it on `kind`. `after`
  // targets the NEXT step, so it sits between this lesson and the following one.
  return {
    sortKey: isAfterAnchor(anchor)
      ? [position.level, position.step + 1, 0, order]
      : [position.level, position.step, 0, order],
    lessonGroupIndex: position.lessonGroupIndex,
  };
}

// A quiz asks about its options, so it never plays before every option has been taught: it lands
// at its anchor or right after the last option's lesson, whichever comes later. Scripts order
// lessons differently (traditional teaches 𠃊 in Level 7, after 𠃌 in Level 3), so one anchor
// cannot promise this alone. A quiz with an option no lesson in this curriculum teaches is left out.
function resolveQuizPlacement(
  entry: QuizManifestEntry,
  positions: LessonPositions,
): { sortKey: SortKey; lessonGroupIndex: number } | null {
  const anchorPlacement = resolveAnchorPlacement(entry, positions);
  if (anchorPlacement === null) {
    return null;
  }
  let placement = anchorPlacement;
  for (const option of entry.options) {
    const position = positions.characters.get(option.glyph);
    if (position === undefined) {
      return null;
    }
    const afterOption: SortKey = [position.level, position.step + 1, 0, entry.order];
    if (compareSortKeys(afterOption, placement.sortKey) > 0) {
      placement = { sortKey: afterOption, lessonGroupIndex: position.lessonGroupIndex };
    }
  }
  return placement;
}

const getUnitSequenceBuilder = memoize(
  (slideManifest: readonly SlideManifestEntry[]) =>
    memoize(
      (quizManifest: readonly QuizManifestEntry[]) =>
        memoize(
          (levels: readonly Level[]) => createUnitSequence(levels, slideManifest, quizManifest),
          { cache: new WeakMap() },
        ),
      { cache: new WeakMap() },
    ),
  { cache: new WeakMap() },
);

/**
 * The lesson sequence for a curriculum. Every input is bundled data that keeps its identity for
 * the app's lifetime (the script's levels array and the slide and quiz manifests), and every destination
 * resolution and next-lesson read walks the sequence — several times per swipe past a slide — so it
 * is built once per pair and shared. Treat it as read-only. Weakly keyed, so a derived levels array
 * (the free tier's capped one) is released with it.
 */
export function buildUnitSequence(
  levels: readonly Level[],
  slideManifest: readonly SlideManifestEntry[] = getSlideManifest(),
  quizManifest: readonly QuizManifestEntry[] = getQuizManifest(),
): readonly SequenceEntry[] {
  return getUnitSequenceBuilder(slideManifest)(quizManifest)(levels);
}

function createUnitSequence(
  levels: readonly Level[],
  slideManifest: readonly SlideManifestEntry[],
  quizManifest: readonly QuizManifestEntry[],
): readonly SequenceEntry[] {
  const entries: SequenceEntry[] = [];

  for (const level of levels) {
    for (const [characterIndex, character] of level.characters.entries()) {
      entries.push({
        character,
        level,
        part: getGraphemeLessonPart({ grapheme: character, isPronunciationLesson: false, level }),
        // `Level.index` is 0-based; sequence level is `index + 1`. `step` is 1-based.
        // Onboarding and other `before` slides share the character's step at kind 0.
        sortKey: [level.index + 1, characterIndex + 1, 1, 0],
        type: "morpheme",
      });
    }
    // Past the last character's step, so a slide anchored `after` it (step + 1, kind 0) still
    // plays before the pronunciation level begins.
    for (const [pronunciationIndex, character] of (level.pronunciationCharacters ?? []).entries()) {
      const hardcoded = hardcodedPronunciationReadingAtSlot({
        afterLevel: level.index + 1,
        grapheme: character,
        slotIndex: pronunciationIndex,
      });
      entries.push({
        character,
        level,
        part: "pronunciation",
        ...(hardcoded === undefined ? {} : { pinyin: hardcoded.pinyin }),
        sortKey: [level.index + 1, level.characters.length + pronunciationIndex + 1, 1, 0],
        type: "morpheme",
      });
    }
  }

  const positions = buildLessonPositions(levels);
  for (const manifestEntry of slideManifest) {
    const placement = resolveAnchorPlacement(manifestEntry, positions);
    if (placement === null) {
      continue;
    }
    entries.push({
      key: manifestEntry.key,
      slug: manifestEntry.slug,
      sortKey: placement.sortKey,
      type: "slide",
      lessonGroupIndex: placement.lessonGroupIndex,
    });
  }
  for (const manifestEntry of quizManifest) {
    const placement = resolveQuizPlacement(manifestEntry, positions);
    if (placement === null) {
      continue;
    }
    entries.push({
      key: manifestEntry.key,
      slug: manifestEntry.slug,
      sortKey: placement.sortKey,
      type: "quiz",
      lessonGroupIndex: placement.lessonGroupIndex,
    });
  }

  entries.sort((a, b) => compareSortKeys(a.sortKey, b.sortKey));
  return entries;
}

/**
 * Sequence indices of morpheme entries that repeat an earlier lesson, keyed the way the resolver
 * dedups (PASS 1 below): a meaning lesson by character, a pronunciation lesson by its pinned
 * reading (or its character when unpinned). Built once per sequence, which is shared and
 * read-only (see {@link buildUnitSequence}).
 */
const getRepeatedLessonIndices = memoize(
  (sequence: readonly SequenceEntry[]): ReadonlySet<number> => {
    const seenLessonKeys = new Set<string>();
    const repeatedIndices = new Set<number>();
    for (const [index, entry] of sequence.entries()) {
      if (entry.type !== "morpheme") {
        continue;
      }
      const lessonKey =
        entry.part !== "pronunciation"
          ? `meaning:${entry.character}`
          : `pronunciation:${
              entry.pinyin === undefined
                ? entry.character
                : pronouncedReadingKey({ grapheme: entry.character, pinyin: entry.pinyin })
            }`;
      if (seenLessonKeys.has(lessonKey)) {
        repeatedIndices.add(index);
      } else {
        seenLessonKeys.add(lessonKey);
      }
    }
    return repeatedIndices;
  },
  { cache: new WeakMap() },
);

/**
 * The slides (and quizzes) the learner has already moved PAST, and so must never be shown again.
 *
 * A slide's anchor names a moment in the curriculum ("after 二", "at the start of level 2").
 * The manifest gives it a position; this decides whether that position is still ahead of the
 * learner. A slide is stale when ANY character positioned at-or-after it has already been
 * studied — that is proof the learner walked through this point in the sequence before, so
 * the teaching moment has passed.
 *
 * Why position and not completion: completion (`user_slide`) records whether the slide was
 * *watched*, which is a different question and is wiped by "reset onboarding". Studied
 * characters (`user_card`) are the durable record of how far the learner actually got, and
 * reset deliberately does not touch them. Gating on position therefore means a reset replays
 * only the slides whose moment is still ahead — someone who already knows 二 does not sit
 * through the 二 slides again, while a genuinely new learner still sees everything in turn.
 *
 * Only a lesson's FIRST occurrence is a position, because the resolver only ever offers that one.
 * Counting a later repeat would turn studying 丨 into "already past" the repeating level, staling
 * every slide behind it. When Starter still re-taught the Ready pack's characters, new learners
 * skipped `basic-strokes` and the rest of Level 1's slides this way. Later packs no longer repeat
 * Ready, so this guards against any future repeat.
 *
 * The scan walks the sequence BACKWARDS, tracking whether any studied character has been seen
 * yet. That makes it a single O(n) pass with no per-slide lookahead, and it keeps the whole
 * thing synchronous so PASS 1 below stays await-free (see the note there).
 */
export function findStaleSlideKeys(
  sequence: readonly SequenceEntry[],
  studiedGraphemes: Set<string>,
  pronouncedGraphemes: PronouncedGraphemes | ReadonlySet<string> = new Set(),
): Set<string> {
  const pronounced = asPronouncedGraphemes(pronouncedGraphemes);
  const repeatedLessonIndices = getRepeatedLessonIndices(sequence);
  const staleKeys = new Set<string>();
  let studiedCharacterFollows = false;

  for (let index = arrayLength(sequence) - 1; index >= 0; index--) {
    const entry = sequence[index];
    if (entry === undefined) {
      continue;
    }

    if (entry.type === "morpheme") {
      if (
        !repeatedLessonIndices.has(index) &&
        isSequenceEntryLearned(entry, studiedGraphemes, pronounced)
      ) {
        studiedCharacterFollows = true;
      }
      continue;
    }

    // A studied character sits somewhere after this slide, so the learner already walked
    // past this point — the slide's moment has gone. When nothing after it is studied the
    // moment is still ahead, and the slide stays eligible.
    if (studiedCharacterFollows) {
      staleKeys.add(entry.key);
    }
  }

  return staleKeys;
}

// Optional overrides for the next-lesson resolution. `afterGrapheme` anchors the scan POSITIONALLY:
// the resolver returns the first unstudied lesson whose position in the fixed sequence is strictly
// after that grapheme's entry (identified by `grapheme` + `levelIndex`, since a grapheme can appear
// in more than one level). This is used to PREDICT the lesson *after* the current card without
// depending on whether the current card's `user_card` has been written yet — the answer comes from
// the static sequence, not the live studied set, so it's immune to the write→read propagation lag.
// The lesson page mounts this with the current grapheme+level to preload the next card, and "Next
// Card" resolves the same anchor at press time. Omit it (the default) to resolve the first unstudied
// lesson from the top of the sequence. `part: "pronunciation"` anchors on the grapheme's
// pronunciation-level lesson, which shares its level with the level's own characters.
export type NextLessonOverride = {
  afterGrapheme?: { grapheme: string; levelIndex: number; part?: GraphemeLessonPart };
  /**
   * Catalogue UUID keys of slides and quizzes to count as done although that may not be written
   * yet: the slide or quiz the learner is continuing past, and (for lookahead) the ones before it.
   * Without it, a prediction made while the learner is still on the last slide of a run (or on a
   * quiz whose answer is still being saved) resolves to that same unit.
   */
  assumedCompletedUnitKeys?: readonly string[];
  /**
   * Characters whose meaning lesson counts as studied although its enrollment may not be written
   * yet: for lookahead, the lesson cards before the one the prediction starts from. Without it, a
   * prediction made on a lesson for the card after the NEXT one (a slide, say) resolves back to
   * that same lesson.
   */
  assumedStudiedGraphemes?: readonly string[];
  /**
   * Override the viewer's premium entitlement, which decides whether the catalogue is capped at
   * the free-tier levels ({@link getAccessibleLevels}). Defaults to the live `useIsPremium` verdict; pass it explicitly
   * only where the caller needs a tier the current session does not have (tests that exercise the
   * full-catalogue scan).
   */
  isPremium?: boolean;
};

export function useGetNextLessonQueryKey() {
  const contentAvailability = useContentAvailability();
  const traditionalCharactersEnabled = useTraditionalCharactersEnabled();
  // Free users are not offered lessons past the free tier's level boundary. Without this the
  // resolver kept handing out post-free-tier lessons that `useFreeTierGate` then refused at the reveal
  // step, so the learner was walked into a paywall instead of being told they had finished
  // everything available to them. See `learn-summary-page-content`, which turns a genuinely empty
  // catalogue into the free-tier completion screen.
  const isPremium = useIsPremium();
  const getMorphemeDetailsTileSliceQueryKey = useGetMorphemeDetailsTileSliceQueryKey();
  const studiedGraphemesQueryOptions = useStudiedGraphemesQueryOptions();
  const pronouncedGraphemesQueryOptions = usePronouncedGraphemesQueryOptions();
  const deferredGraphemesQueryOptions = useDeferredGraphemesQueryOptions();
  const completedSlideKeysQueryOptions = useCompletedSlideKeysQueryOptions();
  const answeredQuizKeysQueryOptions = useAnsweredQuizKeysQueryOptions();
  const actorUserDataQueryOptions = useActorUserDataQueryOptions();
  // Learners may introduce only so many new graphemes per day; the count resets at local
  // midnight, which the `localDayKey` below also covers.
  const graphemesIntroducedTodayQueryOptions = useGraphemesIntroducedTodayQueryOptions();
  // A "skip until tomorrow" expires at local midnight with no table write to invalidate on, so the
  // local day is part of the key: the day rolling over (or the app returning to the foreground on
  // a new day) re-resolves with the released card back in contention.
  const localDayKey = useLocalDayKey();
  const withTableChangeVersion = useTableChangeVersion(tableNameMatcher(NEXT_LESSON_TABLES));

  return ({
    afterGrapheme,
    assumedCompletedUnitKeys,
    assumedStudiedGraphemes,
    isPremium: isPremiumOverride,
  }: NextLessonOverride = {}) =>
    withTableChangeVersion([
      "nextLesson",
      {
        actorUserDataQueryOptions,
        afterGrapheme:
          afterGrapheme === undefined
            ? null
            : ([
                afterGrapheme.grapheme,
                afterGrapheme.levelIndex,
                afterGrapheme.part ?? null,
              ] as const),
        answeredQuizKeysQueryOptions,
        assumedCompletedUnitKeys:
          assumedCompletedUnitKeys === undefined || assumedCompletedUnitKeys.length === 0
            ? null
            : uniq([...assumedCompletedUnitKeys]).sort(),
        assumedStudiedGraphemes:
          assumedStudiedGraphemes === undefined || assumedStudiedGraphemes.length === 0
            ? null
            : uniq([...assumedStudiedGraphemes]).sort(),
        completedSlideKeysQueryOptions,
        contentAvailability,
        deferredGraphemesQueryOptions,
        getMorphemeDetailsTileSliceQueryKey,
        graphemesIntroducedTodayQueryOptions,
        isPremium: isPremiumOverride ?? isPremium,
        localDayKey,
        morphemeDetailsTileSliceQueryFn,
        pronouncedGraphemesQueryOptions,
        studiedGraphemesQueryOptions,
        traditionalCharactersEnabled,
      },
    ] as const);
}

type NextLessonQueryData = ReturnType<ReturnType<typeof useGetNextLessonQueryKey>>[1];

/** Local curriculum position only; content availability is validated before navigation. */
export type NextLessonCandidate =
  | {
      graphemeCard: { front: string };
      level: Level;
      part?: GraphemeLessonPart;
      /** Single-reading 2-P1 slot (干 gan4, then 干 gan1). */
      pinyin?: string;
      type: "morpheme";
    }
  | Extract<Unit, { type: "quiz" }>
  | Extract<Unit, { type: "slide" }>;

async function readNextLessonCandidates(client: QueryClient, options: NextLessonQueryData) {
  const {
    actorUserDataQueryOptions,
    afterGrapheme,
    answeredQuizKeysQueryOptions,
    assumedCompletedUnitKeys,
    assumedStudiedGraphemes,
    completedSlideKeysQueryOptions,
    contentAvailability,
    deferredGraphemesQueryOptions,
    graphemesIntroducedTodayQueryOptions,
    isPremium,
    pronouncedGraphemesQueryOptions,
    studiedGraphemesQueryOptions,
    traditionalCharactersEnabled,
  } = options;
  // Use `fetchQuery` (not `getQueryData(key) ?? fetchQuery`): freshness is now driven by
  // invalidation, so a child whose watcher fired is marked stale but its cached data is still
  // present — a bare `getQueryData` would read that stale set and skip the refetch. `fetchQuery`
  // returns the cache when fresh and refetches when stale/invalidated, so this fan-out stays
  // consistent with its children when any of their tables is written.
  // These actor reads are independent. Queue them together so neither local reads nor web
  // transport batching must wait for the user-data fan-out before loading lesson progress.
  const [
    actorUserData,
    resolvedStudiedGraphemes,
    resolvedCompletedSlideKeys,
    deferredUntilByGrapheme,
    graphemesIntroducedTodayCount,
    pronouncedGraphemes,
    answeredQuizKeys,
  ] = await Promise.all([
    client.fetchQuery(actorUserDataQueryOptions),
    client.fetchQuery(studiedGraphemesQueryOptions),
    client.fetchQuery(completedSlideKeysQueryOptions),
    client.fetchQuery(deferredGraphemesQueryOptions),
    client.fetchQuery(graphemesIntroducedTodayQueryOptions),
    client.fetchQuery(pronouncedGraphemesQueryOptions),
    client.fetchQuery(answeredQuizKeysQueryOptions),
  ]);
  const { user } = actorUserData;
  // Capped BEFORE the sequence is built, so both morpheme lessons and the slides anchored
  // inside the capped range disappear together for a free learner — a slide taught after a
  // character they cannot study has no teaching moment left to land on.
  const allLevels =
    traditionalCharactersEnabled && user.hanziScript === "traditional"
      ? getTraditionalLevels()
      : getSimplifiedLevels();
  const levels = getAccessibleLevels(allLevels, isPremium);

  const studiedGraphemes = new Set(resolvedStudiedGraphemes);
  // Lookahead past lesson cards whose enrollment is not written yet: they are about to be
  // introduced, so they count as studied and toward today's allowance below.
  let pendingAssumedIntroductions = 0;
  for (const grapheme of assumedStudiedGraphemes ?? []) {
    if (!studiedGraphemes.has(grapheme)) {
      studiedGraphemes.add(grapheme);
      pendingAssumedIntroductions += 1;
    }
  }

  // A learner who has introduced today's allowance of new graphemes (their Daily New Cards limit,
  // held to the free tier's cap) is offered no further character lessons until tomorrow (the learn
  // summary explains why). The
  // positional anchor predicts the lesson AFTER the current card before that card is enrolled, so
  // an anchor still unstudied is counted as about to be introduced — otherwise the prediction
  // would hand out one lesson past the limit.
  // A pronunciation-level lesson introduces no new grapheme, so it never counts.
  const pendingIntroductionCount =
    pendingAssumedIntroductions +
    (afterGrapheme !== null &&
    afterGrapheme[2] !== "pronunciation" &&
    !studiedGraphemes.has(afterGrapheme[0])
      ? 1
      : 0);
  const isDailyNewGraphemeLimitReached =
    remainingDailyNewGraphemes({
      introducedTodayCount: graphemesIntroducedTodayCount + pendingIntroductionCount,
      limit: resolveDailyNewGraphemeLimit({
        dailyNewGraphemeLimit: user.dailyNewGraphemeLimit,
        isPremium,
      }).limit,
    }) === 0;

  // "Skip until tomorrow" splits the deferred set in two at FETCH time (the day key above only
  // decides when to re-run; the clock decides the answer):
  //   * ACTIVE — the boundary is still ahead. The character is held out of contention exactly
  //     as if it were unavailable, and so are the slides anchored after it: a slide is a teaching
  //     moment that follows its character, and must not play for one the learner asked to see
  //     tomorrow.
  //   * EXPIRED — the boundary has passed and enrollment never cleared it. The learner is owed
  //     this character, and it is served even from BEFORE a positional anchor: "Next Card" on a
  //     later lesson would otherwise leave it stranded behind the frontier until some unanchored
  //     resolution happened to scan from the top.
  const now = new Date();
  const activelyDeferredGraphemes = new Set<string>();
  const expiredDeferralGraphemes = new Set<string>();
  for (const [grapheme, deferredUntil] of deferredUntilByGrapheme) {
    if (isGraphemeDeferralActive(deferredUntil, now)) {
      activelyDeferredGraphemes.add(grapheme);
    } else {
      expiredDeferralGraphemes.add(grapheme);
    }
  }
  // Slides and quizzes share one key namespace, so one set marks both done.
  const completedUnitKeys = new Set([
    ...resolvedCompletedSlideKeys,
    ...getPendingCompletedSlideKeys(),
    ...answeredQuizKeys,
    ...(assumedCompletedUnitKeys ?? []),
  ]);

  const sequence = buildUnitSequence(levels);
  const availableCharacterSet = new Set(
    levels
      .flatMap((level) => level.characters)
      .filter(
        (character) => contentAvailability.isOnline || contentAvailability.graphemes.has(character),
      ),
  );
  const seenCharacters = new Set<string>();

  // A slide is a teaching moment tied to a POINT in the curriculum, not a checklist item.
  // It must only play when the learner actually arrives at that point — i.e. when the
  // character it is anchored to has just been learned. Once they are past it, the slide is
  // stale forever: replaying "here's what pinyin is" to someone hundreds of characters in is
  // noise, not onboarding. See {@link findStaleSlideKeys}.
  const staleSlideKeys = findStaleSlideKeys(sequence, studiedGraphemes, pronouncedGraphemes);

  // Positional anchor: `afterGrapheme` names the current card's morpheme entry (grapheme +
  // level, since a grapheme can recur across levels). The scan returns the first eligible lesson
  // whose sequence index is strictly GREATER — i.e. the card after the current one — regardless
  // of whether the current card has been written to `user_card` yet. When omitted (or the anchor
  // is not found), `anchorIndex` stays `-1`, so the scan starts from the top of the sequence.
  const anchorIndex =
    afterGrapheme === null
      ? -1
      : sequence.findIndex(
          (entry) =>
            entry.type === "morpheme" &&
            entry.character === afterGrapheme[0] &&
            entry.level.index === afterGrapheme[1] &&
            (entry.part === "pronunciation") === (afterGrapheme[2] === "pronunciation"),
        );

  // PASS 1 — fully synchronous prefilter. Walk the sequence and collect the morpheme
  // candidates that could be returned, in order, doing all the cheap set-based rejection
  // (dedup / unavailable / already studied / at-or-before the anchor) up front. A slide needs
  // no async data, so if one wins positionally we can return it here and never touch the DB.
  //
  // This pass MUST stay await-free: it is what makes the expensive card lookups below
  // batchable. Interleaving an `await` per entry (as this loop used to) put every lookup in
  // its own microtask tick, so the DataLoader behind `morphemeDetailsTileSliceQueryFn`
  // could never coalesce and each read also re-entered the write-clock read guard
  // individually. Near the end of a level that degenerated into thousands of serialized
  // round-trips and froze the web app for the whole press (`千`, the last character of level
  // 3, has 3561 sequence entries after it).
  const candidates: {
    character: string;
    level: Level;
    part?: GraphemeLessonPart;
    pinyin?: string;
  }[] = [];
  const seenPronunciationKeys = new Set<string>();
  // Set once the scan passes an UNSTUDIED character the learner has not reached yet — a
  // collected candidate, or an actively deferred one. Slides after that point have not been
  // earned and are skipped for this resolution.
  let hasUnreachedCharacterBefore = false;

  for (const [index, entry] of sequence.entries()) {
    // A quiz is a teaching moment like a slide: it plays when the learner reaches it, once.
    if (entry.type === "slide" || entry.type === "quiz") {
      // A morpheme collected earlier in this scan sits BEFORE this slide in the sequence, so
      // it is what the learner is due next — the slide's moment comes after that character is
      // taught. Returning the slide here is what put "Character Meanings" (anchored `after: 一`)
      // on screen before 一 had ever been introduced: the character was already sitting in
      // `candidates`, waiting for PASS 2, while this branch returned out from under it.
      //
      // `continue`, NOT `break`: a candidate only WINS if PASS 2 finds it an available card.
      // Every candidate here may still turn out to be card-less (the resolver's documented
      // skip-when-no-card path), and breaking would strand the scan with nothing left to
      // return. Keep collecting later candidates; just stop letting slides jump the queue.
      //
      // An actively deferred character counts the same way: it is unstudied and ahead of the
      // learner, so the slides anchored after it wait for it too.
      if (hasUnreachedCharacterBefore) {
        continue;
      }

      // Entries at or before the anchor are the current-and-prior cards; never return them,
      // but slides carry no character so there is no dedup bookkeeping to preserve here.
      // A stale slide (the learner is already past its anchor) is skipped regardless of
      // completion — this is what keeps "reset onboarding" from replaying the whole
      // catalogue at a learner who only ever needed the slides they have already passed.
      if (
        index > anchorIndex &&
        !completedUnitKeys.has(entry.key) &&
        !staleSlideKeys.has(entry.key)
      ) {
        // Nothing is pending ahead of it (the guard above), and slides and quizzes resolve
        // synchronously with no card lookup — so this unit is the answer.
        return anchoredUnitFromSequenceEntry(entry);
      }
      continue;
    }

    // A pronunciation-level lesson follows its owning level's meanings and ends once the reading
    // that lesson introduces is in study. Its example grapheme does not have to have a meaning card
    // yet: authored pronunciation syllabi may deliberately preview readings from later levels (the
    // final Ready section is "Level 9 readings"). Earlier unstudied meaning candidates still win by
    // sequence order, so this never jumps an unfinished owning level. It is never deferred itself.
    // Hardcoded opening slots that share a grapheme (干 gan4, then 干 gan1) each complete independently.
    if (entry.part === "pronunciation") {
      const pronunciationKey =
        entry.pinyin === undefined
          ? entry.character
          : pronouncedReadingKey({ grapheme: entry.character, pinyin: entry.pinyin });
      const isPronunciationLearned = isSequenceEntryLearned(
        entry,
        studiedGraphemes,
        pronouncedGraphemes,
      );
      if (
        isPronunciationLearned ||
        seenPronunciationKeys.has(pronunciationKey) ||
        (!contentAvailability.isOnline && !contentAvailability.graphemes.has(entry.character))
      ) {
        seenPronunciationKeys.add(pronunciationKey);
        continue;
      }
      seenPronunciationKeys.add(pronunciationKey);
      if (index <= anchorIndex) {
        continue;
      }
      hasUnreachedCharacterBefore = true;
      candidates.push({
        character: entry.character,
        level: entry.level,
        part: "pronunciation",
        ...(entry.pinyin === undefined ? {} : { pinyin: entry.pinyin }),
      });
      continue;
    }

    // Skip duplicate characters (a grapheme can appear in more than one level)
    // and characters that have no morpheme card available. This bookkeeping runs even for
    // entries at/before the anchor so a later duplicate of an already-seen character stays
    // suppressed — the scan only ever returns a character's first sequence occurrence.
    if (
      seenCharacters.has(entry.character) ||
      !availableCharacterSet.has(entry.character) ||
      studiedGraphemes.has(entry.character)
    ) {
      seenCharacters.add(entry.character);
      continue;
    }
    seenCharacters.add(entry.character);

    // Held back until tomorrow: not a candidate, but still an unreached character for the
    // purposes of the slides that follow it (see `hasUnreachedCharacterBefore`).
    if (activelyDeferredGraphemes.has(entry.character)) {
      if (index > anchorIndex) {
        hasUnreachedCharacterBefore = true;
      }
      continue;
    }

    // Positionally before/at the current card — record it as seen (above) but don't return it,
    // UNLESS it is a deferral that has since expired: that character was explicitly set aside,
    // and it is served again as soon as its day arrives, wherever the learner has moved on to.
    if (index <= anchorIndex && !expiredDeferralGraphemes.has(entry.character)) {
      continue;
    }

    hasUnreachedCharacterBefore = true;
    candidates.push({ character: entry.character, level: entry.level, part: entry.part });
  }

  // Past the daily limit no new character is a candidate. A pronunciation-level lesson introduces
  // no new character, so it still is — but only ahead of every held-back character lesson: the
  // pronunciation level never jumps a meaning lesson the curriculum places before it. Slides were
  // still resolved above: one the learner has already reached teaches what they know, and one
  // anchored after a character they have not learned yet stays behind it exactly as before.
  const firstCharacterCandidateIndex = candidates.findIndex(
    (candidate) => candidate.part !== "pronunciation",
  );
  return {
    candidates: isDailyNewGraphemeLimitReached
      ? candidates.slice(
          0,
          firstCharacterCandidateIndex === -1 ? candidates.length : firstCharacterCandidateIndex,
        )
      : candidates,
    type: "candidates",
  } as const;
}

function useGetNextLessonCandidateQueryKey() {
  const getKey = useGetNextLessonQueryKey();
  const withTableChangeVersion = useTableChangeVersion(tableNameMatcher(NEXT_LESSON_TABLES));
  return () => {
    const key = getKey();
    return withTableChangeVersion(["nextLessonCandidate", key[1]] as const);
  };
}

export const [useNextLessonCandidateQueryOptions] = createUseQueryOptions(
  useGetNextLessonCandidateQueryKey,
  async ({ client, queryKey }): Promise<NextLessonCandidate | null> => {
    const selection = await readNextLessonCandidates(client, queryKey[1]);
    if (selection.type !== "candidates") {
      return selection;
    }
    const candidate = selection.candidates[0];
    return candidate === undefined
      ? null
      : {
          graphemeCard: { front: candidate.character },
          level: candidate.level,
          ...(candidate.part === undefined ? {} : { part: candidate.part }),
          ...(candidate.pinyin === undefined ? {} : { pinyin: candidate.pinyin }),
          type: "morpheme",
        };
  },
);

export const [useNextLessonQueryOptions, nextLessonQueryFn] = createUseQueryOptions(
  useGetNextLessonQueryKey,
  async ({ client, queryKey }) => {
    const { getMorphemeDetailsTileSliceQueryKey, morphemeDetailsTileSliceQueryFn } = queryKey[1];
    const selection = await readNextLessonCandidates(client, queryKey[1]);
    if (selection.type !== "candidates") {
      return selection;
    }
    const { candidates } = selection;
    // PASS 2 — try the first candidate alone: normally it exists, and loading 63 later cards
    // (including their pronunciation, mnemonic, and static metadata joins) delays the same
    // answer and retains unused query entries on every cold launch. Missing content still falls
    // back to bounded batches, preserving the fast worst-case scan near catalogue boundaries.
    // Every lookup in a fallback chunk is
    // issued in the same tick, so the DataLoader coalesces them into batched `WHERE grapheme IN
    // (…)` reads instead of one query per character. We still return the FIRST candidate in
    // sequence order with a non-null card, so behavior is identical to the old per-entry scan —
    // only the number of round-trips changes.
    //
    // Chunking (rather than one `Promise.all` over all ~3.5k candidates) keeps us from queueing
    // the entire tail of the catalogue when the answer is almost always in the first chunk: the
    // common case resolves after one lookup, and the worst case (nothing left to study) costs
    // one initial lookup plus ceil((n - 1) / CANDIDATE_CHUNK_SIZE) batched steps rather than n.
    for (let offset = 0; offset < arrayLength(candidates);) {
      const chunkSize = offset === 0 ? 1 : CANDIDATE_CHUNK_SIZE;
      const chunk = candidates.slice(offset, offset + chunkSize);
      offset += chunkSize;

      const resolvedChunk = await Promise.all(
        chunk.map(async (candidate) => ({
          candidate,
          graphemeCard: await client.fetchQuery(
            queryOptions({
              queryFn: morphemeDetailsTileSliceQueryFn,
              queryKey: getMorphemeDetailsTileSliceQueryKey({ morpheme: candidate.character }),
            }),
          ),
        })),
      );

      for (const { candidate, graphemeCard } of resolvedChunk) {
        if (graphemeCard === null) {
          continue;
        }
        return {
          graphemeCard,
          level: candidate.level,
          ...(candidate.part === undefined ? {} : { part: candidate.part }),
          ...(candidate.pinyin === undefined ? {} : { pinyin: candidate.pinyin }),
          type: "morpheme",
        } as const;
      }
    }

    return null;
  },
);

// `collection` remains a refresh trigger for compatibility even though the
// queryFn's mutable reads are `user`, studied cards, and completed slides.
